import axios, { AxiosError, AxiosRequestConfig } from 'axios';

/**
 * RT#9: httpOnly cookies auth — no Authorization header, no localStorage.
 *   - withCredentials: true on all requests (sends cookies)
 *   - 401 response → attempt silent refresh once → retry original
 *   - If refresh fails → notify AuthContext (which sets user=null, ProtectedRoute redirects)
 *
 * VITE_API_BASE_URL: production builds point at the BE origin
 *   (e.g. https://api.your-domain.com). When unset, falls back to '/api'
 *   and relies on the Vite dev proxy (vite.config.ts server.proxy).
 *   Set in Cloudflare Pages env, not committed.
 */
const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '/api';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

let isRefreshing = false;
let refreshSubscribers: Array<(ok: boolean) => void> = [];

/**
 * Auth-failure callback. AuthContext registers a handler that sets user=null
 * so the React Router's ProtectedRoute handles the redirect (no full page reload,
 * no React state loss, no loop).
 *
 * We avoid `window.location.href` because it causes a full page reload that
 * re-fires the AuthProvider's /me request, which on 401 would trigger another
 * refresh attempt — leading to an infinite loop + 429 from the throttler.
 */
let onAuthFailure: (() => void) | null = null;
export function setAuthFailureHandler(handler: (() => void) | null): void {
  onAuthFailure = handler;
}

function notifyAuthFailure(): void {
  onAuthFailure?.();
}

function onRefreshed(ok: boolean): void {
  refreshSubscribers.forEach((cb) => cb(ok));
  refreshSubscribers = [];
}

/**
 * Endpoints whose 401 should NOT trigger a silent refresh:
 * - /admin/auth/login     : wrong password (let the form show the error)
 * - /admin/auth/refresh   : refresh failed (the caller is the refresh itself)
 * - /admin/auth/me        : auth check itself — 401 means "not authed", NOT
 *                           "session expired". Triggering refresh here causes
 *                           an infinite loop on the login page (RT#9 hardening).
 */
const SKIP_REFRESH_PATHS = [
  '/admin/auth/login',
  '/admin/auth/refresh',
  '/admin/auth/me',
];

function shouldSkipRefresh(url: string | undefined): boolean {
  if (!url) return false;
  return SKIP_REFRESH_PATHS.some((p) => url.includes(p));
}

apiClient.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const original = err.config as AxiosRequestConfig & { _retry?: boolean };
    if (
      err.response?.status === 401 &&
      original &&
      !original._retry &&
      !shouldSkipRefresh(original.url)
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshSubscribers.push((ok) => {
            if (ok) {
              original._retry = true;
              resolve(apiClient(original));
            } else {
              reject(err);
            }
          });
        });
      }
      isRefreshing = true;
      try {
        await axios.post(`${API_BASE_URL}/admin/auth/refresh`, {}, { withCredentials: true });
        onRefreshed(true);
        isRefreshing = false;
        original._retry = true;
        return apiClient(original);
      } catch (refreshErr) {
        onRefreshed(false);
        isRefreshing = false;
        // Notify AuthContext → user=null → ProtectedRoute → Navigate('/login').
        // No full page reload (which would re-fire /me and re-loop).
        notifyAuthFailure();
        return Promise.reject(refreshErr);
      }
    }
    return Promise.reject(err);
  },
);
